import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { NotificationService } from '../../services/notification.service';
import { catchError, map, switchMap, withLatestFrom, concatMap, mergeMap } from 'rxjs/operators';
import { Observable, of, from } from 'rxjs';

import { State } from '@completion/reducers';
import { ProjectOverviewService } from '@completion/services';
import {
  ProjectOverviewActionTypes,
  ProjectOverviewLoad,
  ProjectOverviewLoadSuccess,
  ProjectOverviewLoadSystems,
  ProjectOverviewLoadSystemsSuccess,
  ProjectOverviewLoadSystemsFailure,
  ProjectOverviewLoadSystemOverview,
  ProjectOverviewLoadSystemOverviewSuccess,
  ProjectOverviewLoadSystemOverviewFailure,
  OverviewSystemDto,
} from '../actions/project-overview.actions';
import { getCurrentProject } from '@completion/selectors';
import { Project } from '@completion/models';
import { trackResource } from './resource-fetch.util';
import { ProjectOverviewState } from '../reducers/project-overview';

@Injectable()
export class ProjectOverviewEffect {
  constructor(
    private readonly actions$: Actions,
    private readonly service: ProjectOverviewService,
    private readonly store: Store<State>,
    private readonly toastr: NotificationService,
  ) {}

  // Legacy effect kept for compatibility (single heavy call)
  load$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ProjectOverviewLoad>(ProjectOverviewActionTypes.Load),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.service,
        method: 'getProjectOverview',
        serviceArgs: ([_, currentProject]) => [currentProject.id],
        onSuccess: (dtos) => new ProjectOverviewLoadSuccess(dtos as any),
      }),
    ),
  );

  // New flow: load systems first
  loadSystems$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ProjectOverviewLoadSystems>(ProjectOverviewActionTypes.LoadSystems),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      switchMap(([_, project]) =>
        this.service.getOverviewSystems(project.id).pipe(
          map((systems: any[]) => new ProjectOverviewLoadSystemsSuccess(systems as OverviewSystemDto[])),
          catchError((err) => of(new ProjectOverviewLoadSystemsFailure(err?.message || 'Failed to load systems'))),
        ),
      ),
    ),
  );

  // After systems are loaded, emit a LoadSystemOverview for each system that has checksheets
  loadSystemsOverview$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ProjectOverviewLoadSystemsSuccess>(ProjectOverviewActionTypes.LoadSystemsSuccess),
      concatMap((action) => {
        const systemsWithChecks = action.payload.filter((s) => (s as any).checkSheetCount > 0);
        return from(systemsWithChecks).pipe(map((system) => new ProjectOverviewLoadSystemOverview(system)));
      }),
    ),
  );

  // Process LoadSystemOverview actions with limited parallelism (up to 10 systems at a time)
  loadSystemOverview$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ProjectOverviewLoadSystemOverview>(ProjectOverviewActionTypes.LoadSystemOverview),
      withLatestFrom(
        this.store.select(getCurrentProject) as Observable<Project>,
        this.store.select('projectOverview') as Observable<ProjectOverviewState>,
      ),
      mergeMap(([action, project, overviewState]) => {
        const systemId = action.system.id;
        const systemState = overviewState.systems.find((s) => s.id === systemId);

        // If system is already loaded or has no checksheets, skip HTTP call
        if (!systemState || systemState.status === 'loaded' || (systemState.checkSheetCount || 0) === 0) {
          return of();
        }

        return this.service.getOverviewForSystem(project.id, systemId).pipe(
          map((dtos) => new ProjectOverviewLoadSystemOverviewSuccess(systemId, dtos as any[])),
          catchError((err) => of(new ProjectOverviewLoadSystemOverviewFailure(systemId, err?.message || 'Failed to load system overview'))),
        );
      }, 10), // up to 10 concurrent system loads
    ),
  );
}
